import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  GridLegacy as Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoAwesome,
  CheckCircle,
  Close,
  CloudUpload,
  Description,
  ReportProblem,
  Warning,
} from '@mui/icons-material';
import { cvAPI, vendorAPI } from '../../services/api';
import { tokens } from '../../theme';

const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary', 'Transgender', 'Other', 'Prefer not to say'];

const EMPTY_FORM = {
  candidate_name: '',
  candidate_email: '',
  candidate_phone: '',
  gender: '',
  address: '',
  date_of_birth: '',
  age: '',
  total_experience_years: '',
  current_company: '',
  current_designation: '',
  current_location: '',
  expected_salary: '',
  skills: '',
  notes: '',
};

/** Full years between a date string and today, or '' when it is not a usable date. */
const ageFromDob = (dob) => {
  if (!dob) return '';
  const parsed = new Date(dob);
  if (Number.isNaN(parsed.getTime())) return '';
  const today = new Date();
  let years = today.getFullYear() - parsed.getFullYear();
  const monthDayBefore =
    today.getMonth() < parsed.getMonth() ||
    (today.getMonth() === parsed.getMonth() && today.getDate() < parsed.getDate());
  if (monthDayBefore) years -= 1;
  return years > 0 && years < 120 ? String(years) : '';
};

/**
 * Upload a resume, review what was read out of it, and submit the candidate.
 *
 * The sequence is deliberate: the file is sent first and on its own, so the
 * recruiter sees the extracted details and any duplicate warning *before*
 * committing a submission. Every field stays editable — parsing is a first
 * draft, not an authority — and the file is transferred once, with the submit
 * step referring back to it by token.
 */
const ResumeUploadForm = ({ open, onClose, job, requirement, onSubmitted }) => {
  const [file, setFile] = useState(null);
  const [uploadToken, setUploadToken] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [autoFilled, setAutoFilled] = useState({});
  const [parsing, setParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState(0);
  const [parseWarning, setParseWarning] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const title = requirement?.role_title || job?.title || 'this role';

  const reset = useCallback(() => {
    setFile(null);
    setUploadToken(null);
    setForm(EMPTY_FORM);
    setAutoFilled({});
    setParsing(false);
    setParseProgress(0);
    setParseWarning(null);
    setDuplicate(null);
    setSubmitting(false);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const setField = (name) => (event) => {
    const { value } = event.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Age follows the date of birth, so the two can never contradict.
      if (name === 'date_of_birth') {
        const derived = ageFromDob(value);
        if (derived) next.age = derived;
      }
      return next;
    });
    // Once edited by hand, a field is no longer "as parsed".
    setAutoFilled((prev) => (prev[name] ? { ...prev, [name]: false } : prev));
  };

  const handleFileChosen = async (event) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;

    setFile(chosen);
    setError(null);
    setDuplicate(null);
    setParseWarning(null);
    setParsing(true);
    setParseProgress(0);

    try {
      const response = await cvAPI.parseResume(chosen, setParseProgress);
      const { data = {}, upload_token: token, duplicate: dup, parse_warning: warning } = response.data || {};

      setUploadToken(token || null);
      setParseWarning(warning || null);
      setDuplicate(dup && dup.is_duplicate ? dup : null);

      const dob = data.date_of_birth || '';
      const filled = {
        candidate_name: data.name || '',
        candidate_email: data.email || '',
        candidate_phone: data.phone || '',
        gender: GENDER_OPTIONS.includes(data.gender) ? data.gender : '',
        address: data.address || '',
        date_of_birth: dob ? String(dob).slice(0, 10) : '',
        age: data.age != null ? String(data.age) : ageFromDob(dob),
        total_experience_years:
          data.total_experience_years != null ? String(data.total_experience_years) : '',
        current_company: data.current_company || '',
        current_designation: data.current_designation || '',
        current_location: data.current_location || '',
        skills: (data.primary_skills || []).join(', '),
      };

      setForm((prev) => ({ ...prev, ...filled }));
      setAutoFilled(
        Object.entries(filled).reduce((acc, [key, value]) => {
          if (value) acc[key] = true;
          return acc;
        }, {})
      );
    } catch (e) {
      setError(
        e.response?.data?.error ||
          'The resume could not be read. You can still enter the details manually.'
      );
      setUploadToken(null);
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async () => {
    if (!job?.id) {
      setError('No job selected for this submission.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...form };
      if (uploadToken) {
        payload.upload_token = uploadToken;
        payload.original_filename = file?.name || '';
      }
      const response = await vendorAPI.submitCandidate(
        job.id,
        payload,
        uploadToken ? null : file
      );
      onSubmitted?.(response.data);
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not submit the candidate. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const autoFilledCount = useMemo(
    () => Object.values(autoFilled).filter(Boolean).length,
    [autoFilled]
  );

  // Name and email are the two the server insists on, so the button follows suit.
  const canSubmit =
    !!file && !!form.candidate_name.trim() && !!form.candidate_email.trim() && !parsing && !submitting;

  const helper = (name, fallback = ' ') =>
    autoFilled[name] ? 'Read from the resume — edit if wrong' : fallback;

  const fieldProps = (name) => ({
    value: form[name],
    onChange: setField(name),
    helperText: helper(name),
    FormHelperTextProps: autoFilled[name]
      ? { sx: { color: tokens.color.success } }
      : undefined,
  });

  const best = duplicate?.best_match;

  return (
    <Dialog open={!!open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUpload fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }} noWrap>
              Submit a candidate
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }} noWrap>
              {title}
              {requirement?.requirement_code ? ` · ${requirement.requirement_code}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={submitting}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* -- Step 1: the resume ------------------------------------------ */}
        <Box
          sx={{
            border: `1px dashed ${file ? tokens.color.accentBorder : tokens.color.borderStrong}`,
            borderRadius: `${tokens.radius.md}px`,
            bgcolor: file ? tokens.color.accentSoft : tokens.color.surfaceSunken,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Description sx={{ color: tokens.color.inkTertiary }} />
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography variant="subtitle1" noWrap>
              {file ? file.name : 'No resume attached'}
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }}>
              {file
                ? `${(file.size / 1024).toFixed(0)} KB`
                : 'PDF, DOC or DOCX up to 16MB. Details are filled in for you.'}
            </Typography>
          </Box>
          <Button component="label" variant={file ? 'outlined' : 'contained'} disabled={parsing}>
            {file ? 'Replace' : 'Attach resume'}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              hidden
              onChange={handleFileChosen}
            />
          </Button>
        </Box>

        {parsing && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress variant={parseProgress < 100 ? 'determinate' : 'indeterminate'} value={parseProgress} />
            <Typography variant="body2" sx={{ color: tokens.color.inkSecondary, mt: 0.75 }}>
              {parseProgress < 100 ? `Uploading ${parseProgress}%` : 'Reading the resume…'}
            </Typography>
          </Box>
        )}

        {/* -- Duplicate warning ------------------------------------------ */}
        {best && (
          <Alert
            severity={duplicate.severity === 'high' ? 'warning' : 'info'}
            icon={duplicate.severity === 'high' ? <ReportProblem /> : <Warning />}
            sx={{ mt: 2 }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>
              {best.same_recruiter
                ? 'You have already submitted this candidate'
                : 'Another recruiter has already submitted this candidate'}
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {best.reason}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {best.submitted_by_name && (
                <Chip size="small" label={`First submitted by ${best.submitted_by_name}`} />
              )}
              {best.submitted_at && (
                <Chip size="small" label={new Date(best.submitted_at).toLocaleDateString()} />
              )}
              {best.job_title && <Chip size="small" label={best.job_title} />}
              {best.current_stage && <Chip size="small" label={`Stage: ${best.current_stage}`} />}
              <Chip size="small" label={`${Math.round(best.match_score)}% match`} />
            </Box>
            {duplicate.match_count > 1 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {duplicate.match_count} existing records look like this person.
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
              You can still submit. It will be saved and flagged for review.
            </Typography>
          </Alert>
        )}

        {parseWarning && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {parseWarning}
          </Alert>
        )}

        {!parsing && !parseWarning && autoFilledCount > 0 && (
          <Alert severity="success" icon={<AutoAwesome fontSize="small" />} sx={{ mt: 2 }}>
            Filled in {autoFilledCount} {autoFilledCount === 1 ? 'field' : 'fields'} from the resume.
            Check them before submitting.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* -- Step 2: the details ----------------------------------------- */}
        <Divider sx={{ my: 2.5 }}>
          <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
            Candidate details
          </Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Full name" {...fieldProps('candidate_name')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required type="email" label="Email" {...fieldProps('candidate_email')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Phone" {...fieldProps('candidate_phone')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Gender" {...fieldProps('gender')}>
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              {GENDER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Address" {...fieldProps('address')} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Date of birth"
              InputLabelProps={{ shrink: true }}
              {...fieldProps('date_of_birth')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Age"
              inputProps={{ min: 14, max: 99 }}
              {...fieldProps('age', 'From date of birth')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Experience (years)"
              inputProps={{ min: 0, max: 60, step: 0.5 }}
              {...fieldProps('total_experience_years')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Expected salary" {...fieldProps('expected_salary')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Current company" {...fieldProps('current_company')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Current designation" {...fieldProps('current_designation')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Current location" {...fieldProps('current_location')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Skills" {...fieldProps('skills', 'Comma separated')} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes for the reviewer"
              {...fieldProps('notes', 'Optional')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircle fontSize="small" />
          }
        >
          {submitting ? 'Submitting…' : best ? 'Submit anyway (flagged)' : 'Submit candidate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResumeUploadForm;
